import { ReactElement, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import BannerWrapper from '@Hotels-at-Home/hah-fe-core/dist/components/BannerWrapper';
import { IGetPromotionResponse } from '@Hotels-at-Home/hah-fe-core/dist/types';
import BmsmChart from '~/ui/components/common/Banner/BmsmChart';
import Modal from '@Hotels-at-Home/hah-fe-core/dist/components/Modal';
import { getCookie } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';

interface IProps {
  promotion: IGetPromotionResponse;
}

const HeroSection = ({ promotion }: IProps): ReactElement => {
  const { route } = useRouter();
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$heroSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$heroSection' });

  const [showBMSMModal, setShowBMSMModal] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const openPopup = () => {
    setShowBMSMModal(true);
  };

  const closePopup = () => {
    setShowBMSMModal(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      openPopup();
    }
  };

  let RenderBanners;

  const [shouldHideBanner, setShouldHideBanner] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const promoTag = getCookie('promo-tag');
    const hideForPromoTag =
      promoTag === 'air-france-x-sofitel' || promoTag === 'accorhotels-bienvenue';
    setShouldHideBanner(hideForPromoTag);
  }, []);

  const isPostsPage = route.includes('/posts');

  if (shouldHideBanner) {
    RenderBanners = null;
  } else if (promotion?.promo_is_active && promotion?.promo_type !== 'bmsm') {
    RenderBanners = <BannerWrapper promotion={promotion} />;
  } else if (promotion?.promo_is_active && promotion?.promo_type === 'bmsm') {
    RenderBanners = (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-pressed="false"
          className="bmsm-banner-wrapper"
          onClick={openPopup}
          onKeyDown={handleKeyDown}
        >
          <BannerWrapper promotion={promotion} />
        </div>
        <Modal
          open={showBMSMModal}
          maxWidth="sm"
          className="base-banner-lightbox bmsm-lightbox"
          onClose={closePopup}
        >
          <div className="content">
            <BmsmChart />
          </div>
        </Modal>
      </>
    );
  } else {
    RenderBanners = null;
  }

  /* Special Banners logic */ // Banners should end BEFORE BMSM banners shows on 03/28.
  let RenderSpecialBanners;

  const [specialBannersState, setSpecialBannersState] = useState({
    promo_is_active: false,
    promo_type: 'special',
    promo_days_left: 0,
    promo_end: '',
  });

  useEffect(() => {
    const currentDate = new Date().toISOString().split('T')[0];
    const promoEndDate = new Date(new Date().getFullYear(), 2, 26).toISOString().split('T')[0];

    setSpecialBannersState({
      promo_is_active: currentDate < promoEndDate,
      promo_type: 'special',
      promo_days_left: 6,
      promo_end: promoEndDate,
    });
  }, []);
  if (
    !shouldHideBanner &&
    !promotion?.promo_is_active &&
    specialBannersState.promo_is_active &&
    specialBannersState.promo_type === 'special'
  ) {
    RenderSpecialBanners = (
      <a
        href={tLink('linensSateenCollection')}
        target="_blank"
        rel="noreferrer"
        className="special-banner-wrapper"
      >
        <BannerWrapper promotion={specialBannersState} />
      </a>
    );
  } else {
    RenderSpecialBanners = null;
  }

  /* Ends Special Banners logic */

  const shouldRenderBanners = !isPostsPage || isClient;

  return (
    <section className="base-section hero-section main-hp hp-section mybed" id="hp-mybed">
      <div className="hero-section-container">
        <div className="mobile-only">{shouldRenderBanners && RenderBanners}</div>
        <div className="mobile-only">{shouldRenderBanners && RenderSpecialBanners}</div>
        <Link href={tLink('sofitelBed')} noTabbing ariaLabel={`${t('bedding')}`}>
          <div className="hero-video-container desktop-only">
            <div
              className="video-wrapper"
              data-event-name="bloc_interact"
              data-bloc-name="homepage video"
            >
              <video
                muted
                autoPlay
                loop
                playsInline
                className="hp-video"
                width="1920"
                height="730"
                poster="/video/homePage/ArtOfSleep.webp"
              >
                <source src="/video/homePage/ArtOfSleep.webm" type="video/webm" />
                <source src="/video/homePage/ArtOfSleep.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
          <div className="hp-banner mobile-only">
            <picture data-event-name="bloc_interact" data-bloc-name="pillows">
              <source srcSet="/images/misc/fill.gif" media="(min-width: 769px)" />
              <img
                className="lazy loaded"
                data-src="/images/homePage/heroSection/hp-mybed-mob.webp"
                alt={t('title')}
                src="/images/homePage/heroSection/hp-mybed-mob.webp"
                width="800"
                height="550"
              />
            </picture>
          </div>
        </Link>
        <div className="desktop-only">{shouldRenderBanners && RenderBanners}</div>
        <div className="desktop-only">{shouldRenderBanners && RenderSpecialBanners}</div>
        <Link href={tLink('sofitelBed')} noTabbing ariaLabel={`${t('bedding')}`}>
          <div className="stack" data-event-name="bloc_interact" data-bloc-name={t('title')}>
            <div className="top-wrapper">
              <h1 dangerouslySetInnerHTML={{ __html: t('title') }} />
              <span className="btn base-btn btn-main btn-shop-now-inline">
                <span>{tButton('shopNow')}</span>
              </span>
            </div>
            <p className="desktop-only">{t('copyDesktop')}</p>
            <p className="mobile-only">
              <Trans i18nKey="copyMobile" t={t} components={{ span: <span /> }} />
            </p>
            <span className="btn base-btn btn-main btn-shop-now-bottom">
              <span>{tButton('shopNow')}</span>
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
};

export default HeroSection;
